// Student self-signup + student email login (student-self-signup-prd.md §2, §4;
// Phase 1 API contract §3–§8). A student is a normal `role=kid` principal with
// no User row: every login route here returns the SAME kid session envelope as
// the family-code login, and the backend sets the same kid refresh cookie.
//
// Kept apart from `useAuth.ts` so pages that mock `@/auth/useAuth` in their
// tests are not affected by these extra exports.

import { api, ApiError } from '@/lib/api';
import { getSocket } from '@/lib/ws';
import { useAuthStore } from './authStore';
import type { FamilyKind, KidAccountMode, KidLoginResponse, UnifiedSignInResponse } from './types';
import { acceptSignIn } from './unifiedSignIn';

export { isIndependentStudent, isStudentFamily } from './studentAccount';

export const STUDENT_YEAR_LEVEL_MIN = 1;
export const STUDENT_YEAR_LEVEL_MAX = 12;
export const STUDENT_NICKNAME_MAX = 40;
export const STUDENT_PASSWORD_MIN = 8;
export const STUDENT_PASSWORD_MAX = 200;
export const STUDENT_SCHOOL_NAME_MAX = 160;
export const STUDENT_MIN_AGE_DEFAULT = 13;
/** Mirrors the backend `STUDENT_SIGNUP_MAX_AGE`: student accounts are for school students. */
export const STUDENT_MAX_AGE = 19;
/**
 * Student legal safeguards (student-self-signup-prd.md §19, D1): below this age
 * a student ticks that a parent or guardian gave permission for the account.
 * The backend serves the live value as `guardian_permission_under_age`; this is
 * the fallback for an older API.
 */
export const STUDENT_GUARDIAN_PERMISSION_UNDER_AGE_DEFAULT = 15;
/** Under 18 a student also confirms a guardian's permission to buy (D2) and to enter a Challenge (D5). */
export const STUDENT_ADULT_AGE = 18;

/** Same rule as the backend `studentAgeFromBirthMonth`: "today" is the calendar month in Sydney. */
const STUDENT_AGE_TIMEZONE = 'Australia/Sydney';

/** Backend error codes this surface turns into its own copy. */
export const STUDENT_ERROR_CODES = {
  signupDisabled: 'STUDENT_SIGNUP_DISABLED',
  termsVersionMismatch: 'TERMS_VERSION_MISMATCH',
  invalidBirthDate: 'INVALID_BIRTH_DATE',
  underMinAge: 'STUDENT_SIGNUP_UNDER_MIN_AGE',
  overMaxAge: 'STUDENT_SIGNUP_OVER_MAX_AGE',
  invalidOtp: 'INVALID_OTP',
  noAccountForEmail: 'NO_ACCOUNT_FOR_EMAIL',
  emailUsedByAdult: 'EMAIL_USED_BY_ADULT_ACCOUNT',
  studentAccountExists: 'STUDENT_ACCOUNT_EXISTS',
  invalidCredentials: 'INVALID_CREDENTIALS',
  kidPaused: 'KID_PAUSED',
  studentAccountRequired: 'STUDENT_ACCOUNT_REQUIRED',
  studentAccountEmail: 'STUDENT_ACCOUNT_EMAIL',
  rateLimited: 'RATE_LIMITED',
  guardianPermissionRequired: 'GUARDIAN_PERMISSION_REQUIRED',
  paymentPermissionRequired: 'STUDENT_PAYMENT_PERMISSION_REQUIRED',
} as const;

/**
 * GET /auth/student/signup/config (Phase 2 contract §2). `terms_version` is the
 * backend's current `LEGAL_TERMS_VERSION`: signup sends it back as accepted, so
 * Learn never hard-codes it. `parent_link_enabled` is the Phase 2 switch.
 */
export interface StudentSignupConfig {
  enabled: boolean;
  min_age: number;
  parent_link_enabled: boolean;
  terms_version: string;
  /**
   * D1 (student-self-signup-prd.md §19): a student younger than this ticks the
   * guardian-permission box. Optional so an older API still reads; the page then
   * uses `STUDENT_GUARDIAN_PERMISSION_UNDER_AGE_DEFAULT`.
   */
  guardian_permission_under_age?: number;
}

export interface StudentSignupInput {
  email: string;
  /** The signup code — or `signup_ticket` when Learn sign-in already verified the inbox. */
  code?: string;
  signup_ticket?: string;
  nickname: string;
  birth_year: number;
  birth_month: number;
  year_level: number;
  school_name?: string;
  school_suburb?: string;
  school_state?: string;
  school_acara_id?: string;
  /** D1: sent as `true` only when the student is under the guardian-permission age and ticked the box. */
  guardian_permission?: boolean;
  /** Optional password set at sign-up (owner 2026-10-11). */
  password?: string;
  /** ISO 3166-1 alpha-2; students outside Australia can sign up (owner 2026-10-11). */
  country?: string;
}

/** GET /students/me (contract §7). */
export interface StudentProfile {
  id: string;
  nickname: string;
  avatar_id: string | null;
  age: number;
  birth_year: number;
  birth_month: number;
  year_level: number | null;
  school_name: string | null;
  school_suburb: string | null;
  school_state: string | null;
  school_acara_id: string | null;
  login_email: string;
  has_password: boolean;
  account_mode: KidAccountMode;
  family_id: string;
  family_kind: FamilyKind;
  /** D1: when an under-15 student said a guardian gave permission at signup. Optional for an older API. */
  guardian_permission_confirmed_at?: string | null;
  /** D2: when the student confirmed they may pay. `null` until then; every checkout refuses before it. */
  payment_permission_confirmed_at?: string | null;
  /** D2: true while the student is under 18, so the confirmation also needs the guardian tick. */
  payment_permission_needs_guardian?: boolean;
}

/** POST /students/me/payment-permission body (D2). Strict on the server. */
export interface StudentPaymentPermissionInput {
  card_holder_permission: true;
  guardian_permission?: boolean;
}

/** PATCH /students/me body (contract §7). Partial; `null` clears a school field. */
export interface StudentProfilePatch {
  nickname?: string;
  avatar_id?: string;
  year_level?: number;
  school_name?: string | null;
  school_suburb?: string | null;
  school_state?: string | null;
  school_acara_id?: string | null;
}

export const STUDENT_PROFILE_QUERY_KEY = ['students', 'me'] as const;
export const STUDENT_SIGNUP_CONFIG_QUERY_KEY = ['auth', 'student', 'signup-config'] as const;

/**
 * Whole years old today from a birth year + month, treating the 1st of the
 * birth month as the birthday and "today" as the calendar month in Sydney —
 * the backend's `studentAgeFromBirthMonth`. Returns `null` for a future month.
 * The page uses it only to decide which boxes to show; the server decides.
 */
export function studentAgeFromBirthMonth(
  birthYear: number,
  birthMonth: number,
  now: Date = new Date(),
): number | null {
  const parts = new Intl.DateTimeFormat('en-AU', {
    timeZone: STUDENT_AGE_TIMEZONE,
    year: 'numeric',
    month: 'numeric',
  }).formatToParts(now);
  const pick = (type: 'year' | 'month'): number =>
    Number(parts.find((part) => part.type === type)?.value);
  const year = pick('year');
  const month = pick('month');
  if (birthYear > year || (birthYear === year && birthMonth > month)) return null;
  return year - birthYear - (month < birthMonth ? 1 : 0);
}

/** Trim + lower-case, the same canonical form the backend stores. */
export function normaliseStudentEmail(email: string): string {
  return email.trim().toLowerCase();
}

function acceptStudentSession(res: KidLoginResponse): KidLoginResponse {
  useAuthStore.getState().setToken('kid', res.access_token);
  useAuthStore.getState().setBootstrapped(true);
  setTimeout(() => getSocket('kid'), 0);
  return res;
}

// ── Signup (contract §3, §4) ────────────────────────────────────────────────

export async function getStudentSignupConfig(): Promise<StudentSignupConfig> {
  return api<StudentSignupConfig>('/auth/student/signup/config', { skipAuthRefresh: true });
}

/** Always 204 for any email (anti-enumeration); a used email gets a "sign in" email instead. */
export async function requestStudentSignupOtp(email: string): Promise<void> {
  await api<void>('/auth/student/signup/request-otp', {
    method: 'POST',
    body: { email: normaliseStudentEmail(email) },
    skipAuthRefresh: true,
  });
}

/**
 * `termsVersion` is the version the student saw, read from the signup config.
 * A stale one returns `TERMS_VERSION_MISMATCH` with `details.current_version`.
 */
export async function verifyStudentSignup(
  input: StudentSignupInput,
  termsVersion: string,
): Promise<KidLoginResponse> {
  const res = await api<KidLoginResponse>('/auth/student/signup/verify', {
    method: 'POST',
    body: {
      ...input,
      email: normaliseStudentEmail(input.email),
      accept_terms: true,
      terms_version: termsVersion,
    },
    skipAuthRefresh: true,
  });
  return acceptStudentSession(res);
}

/**
 * Learn sign-in found no account for a correctly verified email (404
 * NO_ACCOUNT_FOR_EMAIL). The response carries a short-lived signup ticket so
 * sign-up can continue without a second code.
 */
export function signupTicketFrom(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.code !== STUDENT_ERROR_CODES.noAccountForEmail)
    return null;
  const details = error.details as { signup_ticket?: unknown } | undefined;
  return typeof details?.signup_ticket === 'string' ? details.signup_ticket : null;
}

/** Router state Learn sign-in hands to /learn/signup after NO_ACCOUNT_FOR_EMAIL. */
export interface VerifiedSignupState {
  email: string;
  signupTicket: string;
}

// ── Login (contract §5) ─────────────────────────────────────────────────────

/**
 * Always 204. A code is sent to a live student's login email AND, since the
 * unified sign-in (D-SSU-24), to a parent or teacher email; nothing is sent to
 * an unknown email.
 */
export async function requestStudentLoginOtp(email: string): Promise<void> {
  await api<void>('/auth/student/login/request-otp', {
    method: 'POST',
    body: { email: normaliseStudentEmail(email) },
    skipAuthRefresh: true,
  });
}

/**
 * Unified sign-in (D-SSU-24): a student email answers with the kid session, a
 * parent or teacher email with the ADULT session (`session_kind: 'user'`). The
 * token goes in the slot the server names; the caller routes on the returned
 * `UnifiedSignInResponse` (see `isKidSignIn` / `signInLandingPath`).
 */
export async function verifyStudentLoginOtp(
  email: string,
  code: string,
): Promise<UnifiedSignInResponse> {
  const res = await api<UnifiedSignInResponse>('/auth/student/login/verify-otp', {
    method: 'POST',
    body: { email: normaliseStudentEmail(email), code },
    skipAuthRefresh: true,
  });
  acceptSignIn(res);
  return res;
}

export async function studentPasswordLogin(
  email: string,
  password: string,
): Promise<UnifiedSignInResponse> {
  const res = await api<UnifiedSignInResponse>('/auth/student/login-password', {
    method: 'POST',
    body: { email: normaliseStudentEmail(email), password },
    skipAuthRefresh: true,
  });
  acceptSignIn(res);
  return res;
}

/**
 * Set or change the student's optional password. The backend signs out every
 * other device and returns a NEW kid session for this browser, so the old access
 * token is replaced before anything refetches.
 */
export async function setStudentPassword(password: string): Promise<KidLoginResponse> {
  const res = await api<KidLoginResponse>('/auth/student/set-password', {
    method: 'POST',
    body: { password },
    principal: 'kid',
  });
  useAuthStore.getState().setToken('kid', res.access_token);
  return res;
}

// ── Self-profile (contract §7) ──────────────────────────────────────────────

export async function getStudentProfile(): Promise<StudentProfile> {
  return api<StudentProfile>('/students/me', { principal: 'kid' });
}

export async function updateStudentProfile(patch: StudentProfilePatch): Promise<StudentProfile> {
  return api<StudentProfile>('/students/me', { method: 'PATCH', body: patch, principal: 'kid' });
}

/**
 * D2 (student-self-signup-prd.md §19): the one-time "I may pay" confirmation.
 * Idempotent on the server: a repeat returns the same timestamp. Under 18 it
 * refuses without `guardian_permission: true` (400 GUARDIAN_PERMISSION_REQUIRED).
 */
export async function confirmStudentPaymentPermission(
  input: StudentPaymentPermissionInput,
): Promise<StudentProfile> {
  return api<StudentProfile>('/students/me/payment-permission', {
    method: 'POST',
    body: input,
    principal: 'kid',
  });
}

// ── Copy ────────────────────────────────────────────────────────────────────

/** Reads `details.min_age` from an under-age error; falls back to the default. */
export function minAgeFromError(error: unknown, fallback = STUDENT_MIN_AGE_DEFAULT): number {
  if (!(error instanceof ApiError)) return fallback;
  const details = error.details as { min_age?: unknown } | undefined;
  return typeof details?.min_age === 'number' ? details.min_age : fallback;
}

/**
 * One place for the student-facing error copy. Unknown codes keep the
 * server message (already user-safe per the error envelope), else `fallback`.
 */
export function studentAuthErrorMessage(error: unknown, fallback: string): string {
  if (!(error instanceof ApiError)) return fallback;
  switch (error.code) {
    case STUDENT_ERROR_CODES.signupDisabled:
      return 'Student sign-up is not open right now. You can still sign in if you already have an account.';
    case STUDENT_ERROR_CODES.termsVersionMismatch:
      return 'Our terms were just updated. Read them again, then press Create my account.';
    case STUDENT_ERROR_CODES.invalidBirthDate:
      return 'That birth month is in the future. Check the year and month.';
    case STUDENT_ERROR_CODES.underMinAge:
      return `You need to be at least ${minAgeFromError(error)} to make your own account. Ask a parent to set one up for you.`;
    case STUDENT_ERROR_CODES.overMaxAge:
      return 'Student accounts are for school students. Adults can make a parent account instead.';
    case STUDENT_ERROR_CODES.invalidOtp:
      return 'That code is wrong or has expired. Check your email or send a new code.';
    case STUDENT_ERROR_CODES.noAccountForEmail:
      return 'There is no account for this email yet. Create a student account to get started.';
    case STUDENT_ERROR_CODES.emailUsedByAdult:
      return 'This email is already used by a parent or teacher account. Use a different email.';
    case STUDENT_ERROR_CODES.studentAccountExists:
      return 'You already have a student account with this email. Sign in instead.';
    case STUDENT_ERROR_CODES.invalidCredentials:
      return 'Email or password is wrong. You can sign in with an email code instead.';
    case STUDENT_ERROR_CODES.kidPaused:
      return 'This account is paused. Contact Airbotix support for help.';
    case STUDENT_ERROR_CODES.studentAccountRequired:
      return 'Only student accounts that signed up with email can change this.';
    case STUDENT_ERROR_CODES.rateLimited:
      return 'Too many tries. Wait a few minutes, then try again.';
    case STUDENT_ERROR_CODES.guardianPermissionRequired:
      return 'Tick the box to say a parent or guardian gave you permission.';
    case STUDENT_ERROR_CODES.paymentPermissionRequired:
      return 'Before you buy, confirm that you are allowed to use this payment card.';
    default:
      return error.status === 429
        ? 'Too many tries. Wait a few minutes, then try again.'
        : error.message || fallback;
  }
}
